"use client";

import { useState } from "react";
import { Heart, Share2 } from "lucide-react";
import { INSTRUMENT_CATEGORIES } from "@/data/constants";

interface ProductCoreInfoProps {
  category?: string;
  subcategory?: string;
  brand?: string;
  model?: string;
  title?: string;
  price: number;
  isLiked?: boolean;
  onLike?: () => void;
  onShare?: () => void;
}

export function ProductCoreInfo({
  category,
  subcategory,
  brand,
  model,
  title,
  price,
  isLiked = false,
  onLike,
  onShare,
}: ProductCoreInfoProps) {
  const formatPrice = (price: number): string => {
    return new Intl.NumberFormat("ko-KR").format(price);
  };

  const getCategoryLabel = (catId?: string) => {
    if (!catId) return "";
    const category = INSTRUMENT_CATEGORIES.find((c) => c.id === catId);
    return category?.name || catId;
  };

  const displayTitle = title || `${brand || ""} ${model || ""}`.trim() || "악기";

  return (
    <div className="space-y-4">
      {/* 카테고리 경로 */}
      {(category || subcategory) && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          {category && (
            <>
              <span className="px-3 py-1 bg-[#F4F0FF] text-[#6D4AFF] rounded-full font-medium">
                {getCategoryLabel(category)}
              </span>
              {subcategory && (
                <>
                  <span className="text-gray-400">/</span>
                  <span className="px-3 py-1 bg-[#F4F0FF] text-[#6D4AFF] rounded-full font-medium">
                    {subcategory}
                  </span>
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* 브랜드 · 모델명 */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 leading-tight">
          {displayTitle}
        </h1>
        {brand && model && title && (
          <p className="text-base text-gray-600 mt-1">
            {brand} · {model}
          </p>
        )}
      </div>

      {/* 가격 + 액션 버튼 */}
      <div className="flex items-center justify-between">
        <div className="text-3xl font-bold text-[#6D4AFF]">
          {formatPrice(price)}원
        </div>
        <div className="flex gap-2">
          <button
            onClick={onLike}
            className={`p-3 rounded-2xl border-2 transition-all ${
              isLiked
                ? "bg-red-50 border-red-200 text-red-500"
                : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
            }`}
            aria-label={isLiked ? "찜하기 해제" : "찜하기"}
          >
            <Heart
              className={`w-6 h-6 ${isLiked ? "fill-current" : ""}`}
            />
          </button>
          <button
            onClick={onShare}
            className="p-3 rounded-2xl border-2 border-gray-200 text-gray-600 hover:bg-gray-50 transition-all bg-white"
            aria-label="공유하기"
          >
            <Share2 className="w-6 h-6" />
          </button>
        </div>
      </div>
    </div>
  );
}

